import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Widget } from '../types/widget';
import { WidgetCard } from './WidgetCard';
import { WidgetService } from '../services/widgetService';
import { truncateWidgetTitle } from '../utils/textUtils';
import MetricSection from './MetricSection';
import ChartSection from './ChartSection';
import TableSection from './TableSection';
import { getTenantId, getConnectionId, getAccessToken } from '../utils/storage';
import Toast from './Toast';

// Widget categorization logic
const categorizeWidgets = (widgets: Widget[]) => {
    const metrics: Widget[] = [];
    const charts: Widget[] = [];
    const tables: Widget[] = [];

    widgets.forEach(widget => {
        const type = widget.visualization_type?.toLowerCase() || '';

        // Metric cards: small widgets for KPIs
        if (type.includes('metric') ||
            type.includes('number') ||
            type.includes('summary') ||
            type === 'kpi' ||
            type === 'stat') {
            metrics.push(widget);
        }
        // Charts: bar, pie, line, donut, doughnut, gauge, area charts
        else if (type.includes('chart') ||
            type === 'bar' ||
            type === 'line' ||
            type === 'pie' ||
            type === 'donut' ||
            type === 'doughnut' ||
            type === 'gauge' ||
            type === 'area' ||
            type.includes('graph') ||
            type.includes('plot')) {
            charts.push(widget);
        }
        // Tables: large data widgets
        else if (type.includes('table') ||
            type.includes('list') ||
            type.includes('log') ||
            type.includes('activity') ||
            type === 'data' ||
            type === 'grid') {
            tables.push(widget);
        }
        // Default fallback - put in charts section for medium-sized display
        else {
            charts.push(widget);
        }
    });

    return { metrics, charts, tables };
};



interface WidgetGridProps {
    userId: string;
    collectionId?: string | null;
    reportTab?: string | null;
    onAskMore?: (contextQuery: string, contextData?: any) => void;
    showAll?: boolean;
    readOnly?: boolean;
}

interface WidgetGridRef {
    refresh: () => Promise<void>;
}

// Confirmation Modal Component
const DeleteConfirmationModal: React.FC<{
    isOpen: boolean;
    widgetTitle: string;
    onConfirm: () => void;
    onCancel: () => void;
    isDeleting: boolean;
}> = ({ isOpen, widgetTitle, onConfirm, onCancel, isDeleting }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md">
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mr-4">
                            <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Delete Widget
                        </h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Are you sure you want to delete <span className="font-medium" title={widgetTitle}>"{truncateWidgetTitle(widgetTitle)}"</span>? This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={onCancel}
                            disabled={isDeleting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isDeleting}
                            className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isDeleting && (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            )}
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const WidgetGrid = forwardRef<WidgetGridRef, WidgetGridProps>(({ userId, collectionId, reportTab, onAskMore, showAll = false, readOnly = false }, ref) => {
    const [widgets, setWidgets] = useState<Widget[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSavingOrder, setIsSavingOrder] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);
    const [deleteModal, setDeleteModal] = useState<{
        isOpen: boolean;
        widget: Widget | null;
        isDeleting: boolean;
    }>({
        isOpen: false,
        widget: null,
        isDeleting: false,
    });

    const fetchWidgets = useCallback(async () => {
        if (!collectionId) {
            setWidgets([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const orderedWidgets = await WidgetService.fetchWidgetsOrdered(userId, collectionId, reportTab);
            setWidgets(orderedWidgets);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, [userId, collectionId, reportTab]);

    useEffect(() => {
        fetchWidgets();
    }, [userId, fetchWidgets]);

    // Check for refresh flag when component mounts or becomes visible
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && localStorage.getItem('refreshWidgets') === 'true') {
                localStorage.removeItem('refreshWidgets');
                fetchWidgets();
            }
        };

        // Check immediately on mount
        if (localStorage.getItem('refreshWidgets') === 'true') {
            localStorage.removeItem('refreshWidgets');
            fetchWidgets();
        }

        // Listen for visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchWidgets]);

    // Expose refresh method via ref
    useImperativeHandle(ref, () => ({
        refresh: fetchWidgets
    }));

    const handleDelete = (widget: Widget) => {
        setDeleteModal({
            isOpen: true,
            widget,
            isDeleting: false,
        });
    };

    const handleConfirmDelete = async () => {
        if (!deleteModal.widget) return;

        const widgetTitle = deleteModal.widget.title;
        setDeleteModal(prev => ({ ...prev, isDeleting: true }));

        try {
            const connectionId = getConnectionId();
            const accessToken = getAccessToken();

            const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/widgets/${deleteModal.widget.id}${connectionId ? `?connection_id=${connectionId}` : ''}`, {
                method: 'DELETE',
                headers: {
                    ...(accessToken && { 'Authorization': `Bearer ${accessToken}` })
                }
            });
            if (response.ok) {
                // Remove the deleted widget from state instead of refetching all widgets
                setWidgets(prevWidgets => prevWidgets.filter(w => w.id !== deleteModal.widget?.id));
                setDeleteModal({ isOpen: false, widget: null, isDeleting: false });
                // Show success toast
                setToast({ message: `Widget "${widgetTitle}" deleted successfully!`, type: 'success' });
            } else {
                setDeleteModal(prev => ({ ...prev, isDeleting: false }));
                setToast({ message: 'Failed to delete widget. Please try again.', type: 'error' });
            }
        } catch (err) {
            console.error("Error deleting widget:", err);
            setDeleteModal(prev => ({ ...prev, isDeleting: false }));
            setToast({ message: 'Error deleting widget. Please try again.', type: 'error' });
        }
    };

    const handleCancelDelete = () => {
        setDeleteModal({ isOpen: false, widget: null, isDeleting: false });
    };

    const handleOnDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const { source, destination } = result;

        // Get categorized widgets for proper index mapping
        const { metrics, charts, tables } = categorizeWidgets(widgets);

        // Create a mapping of droppableId to section arrays
        const sections = {
            'metrics-section': metrics,
            'charts-section': charts,
            'tables-section': tables
        };

        // Get source and destination sections
        const sourceSection = sections[source.droppableId as keyof typeof sections];
        const destSection = sections[destination.droppableId as keyof typeof sections];

        if (!sourceSection || !destSection) return;

        // Get the widget being moved
        const sourceWidget = sourceSection[source.index];
        if (!sourceWidget) return;

        // Create a new widgets array for the update
        let updatedWidgets = Array.from(widgets);

        if (source.droppableId === destination.droppableId) {
            // Same section reordering
            // Find the actual indices in the full widgets array
            const sourceGlobalIndex = updatedWidgets.findIndex(w => w.id === sourceWidget.id);
            const destWidget = destSection[destination.index];
            const isMovingDown = source.index < destination.index;

            if (sourceGlobalIndex === -1) return;

            // Remove the source widget
            const [movedWidget] = updatedWidgets.splice(sourceGlobalIndex, 1);

            if (destWidget) {
                // Find destination position in global array
                const destGlobalIndex = updatedWidgets.findIndex(w => w.id === destWidget.id);
                // When moving down, insert AFTER the destination widget
                // When moving up, insert AT the destination widget's position
                const insertIndex = isMovingDown ? destGlobalIndex + 1 : destGlobalIndex;
                updatedWidgets.splice(insertIndex, 0, movedWidget);
            } else {
                // Moving to the end of the section
                // Find the last widget of this section and insert after it
                const sectionWidgets = destSection.filter(w => w.id !== sourceWidget.id);
                if (sectionWidgets.length > 0) {
                    const lastWidget = sectionWidgets[sectionWidgets.length - 1];
                    const lastGlobalIndex = updatedWidgets.findIndex(w => w.id === lastWidget.id);
                    updatedWidgets.splice(lastGlobalIndex + 1, 0, movedWidget);
                } else {
                    // Section will be empty after move, append at end
                    updatedWidgets.push(movedWidget);
                }
            }
        } else {
            // Cross-section movement
            const sourceGlobalIndex = updatedWidgets.findIndex(w => w.id === sourceWidget.id);
            if (sourceGlobalIndex === -1) return;

            // Remove from source
            const [movedWidget] = updatedWidgets.splice(sourceGlobalIndex, 1);

            if (destination.index < destSection.length) {
                // Insert at specific position in destination section
                const destWidget = destSection[destination.index];
                const destGlobalIndex = updatedWidgets.findIndex(w => w.id === destWidget.id);
                updatedWidgets.splice(destGlobalIndex, 0, movedWidget);
            } else {
                // Insert at end of destination section
                if (destSection.length > 0) {
                    const lastWidget = destSection[destSection.length - 1];
                    const lastGlobalIndex = updatedWidgets.findIndex(w => w.id === lastWidget.id);
                    updatedWidgets.splice(lastGlobalIndex + 1, 0, movedWidget);
                } else {
                    // Destination section is empty, find appropriate position
                    const destinationSectionNames = ['metrics-section', 'charts-section', 'tables-section'];
                    const destSectionIndex = destinationSectionNames.indexOf(destination.droppableId);

                    // Insert based on section priority
                    if (destSectionIndex === 0) {
                        // Insert at beginning (metrics first)
                        updatedWidgets.unshift(movedWidget);
                    } else if (destSectionIndex === 1) {
                        // Insert after metrics but before tables
                        const metricsEnd = updatedWidgets.findIndex(w => {
                            const type = w.visualization_type?.toLowerCase() || '';
                            return !(type.includes('metric') || type.includes('number') || type.includes('summary') || type === 'kpi' || type === 'stat');
                        });
                        if (metricsEnd !== -1) {
                            updatedWidgets.splice(metricsEnd, 0, movedWidget);
                        } else {
                            updatedWidgets.push(movedWidget);
                        }
                    } else {
                        // Insert at end (tables last)
                        updatedWidgets.push(movedWidget);
                    }
                }
            }
        }

        // Update display_order for all widgets
        const finalWidgets = updatedWidgets.map((widget, index) => ({
            ...widget,
            display_order: index + 1
        }));

        // Optimistic update - update UI immediately
        setWidgets(finalWidgets);
        setSaveError(null);
        setIsSavingOrder(true);

        try {
            // Save the new order to backend
            const success = await WidgetService.saveWidgetOrder(userId, finalWidgets, collectionId);
            if (!success) {
                throw new Error('Failed to save widget order');
            }
        } catch (error) {
            console.error('Error saving widget order:', error);
            setSaveError('Failed to save widget arrangement');
            // Revert to original order on error
            setWidgets(widgets);
        } finally {
            setIsSavingOrder(false);
        }
    };

    if (loading) {
        return (
            <div className="flex gap-4 w-full p-2">
                {Array.from({ length: 3 }, (_, columnIndex) => (
                    <div key={columnIndex} className="flex-1 flex flex-col gap-4">
                        {[
                            { span: 'col-span-1' }, // Metric card
                            { span: 'col-span-1' }, // Metric card
                            { span: 'col-span-1' }, // Metric card
                            { span: 'col-span-1' }, // Metric card
                            { span: 'col-span-1' }, // Metric card
                            { span: 'col-span-1 md:col-span-2 xl:col-span-2' }, // Chart
                            { span: 'col-span-1 md:col-span-2 xl:col-span-2' }, // Chart
                            { span: 'col-span-1 md:col-span-3 xl:col-span-5' }, // Table
                        ].map((item, i) => (
                            <div key={i} className={`${item.span} bg-gradient-to-br from-white to-slate-50/50 dark:from-slate-800/50 dark:to-slate-900/50 border border-slate-200/60 dark:border-slate-700/60 rounded-xl p-6 shadow-lg backdrop-blur-sm shimmer-container`}>
                                <div className="animate-pulse">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center space-x-3">
                                            <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded-lg shimmer"></div>
                                            <div>
                                                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32 shimmer mb-2"></div>
                                                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-20 shimmer"></div>
                                            </div>
                                        </div>
                                        <div className="flex space-x-2">
                                            <div className="h-6 w-6 bg-slate-200 dark:bg-slate-700 rounded-lg shimmer"></div>
                                            <div className="h-6 w-6 bg-slate-200 dark:bg-slate-700 rounded-lg shimmer"></div>
                                            <div className="h-6 w-6 bg-slate-200 dark:bg-slate-700 rounded-lg shimmer"></div>
                                        </div>
                                    </div>
                                    <div className="mb-4 p-3 bg-slate-100/50 dark:bg-slate-800/50 rounded-lg border-l-3 border-slate-300 dark:border-slate-600">
                                        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full shimmer mb-2"></div>
                                        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-3/4 shimmer"></div>
                                    </div>
                                    <div className="mt-4 h-40 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 rounded-xl shimmer border border-slate-200/60 dark:border-slate-600/60"></div>
                                    <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                                        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-24 shimmer"></div>
                                        <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-20 shimmer"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    }
    // Silently fail on error - just show nothing
    if (error) {
        console.error('WidgetGrid error:', error);
        return null;
    }

    if (widgets.length === 0) {
        return null;
    }

    // Determine which widgets to show
    const visibleWidgets = showAll ? widgets : widgets.slice(0, 12);

    // Categorize widgets into sections
    const { metrics, charts, tables } = categorizeWidgets(visibleWidgets);

    const renderWidget = (widget: Widget, index: number) => {
        // Determine widget section type for styling
        const type = widget.visualization_type?.toLowerCase() || '';
        const isMetric = type.includes('metric') || type.includes('number') || type.includes('summary') || type === 'kpi' || type === 'stat';
        const isTable = type.includes('table') || type.includes('list') || type.includes('log') || type.includes('activity') || type === 'data' || type === 'grid';

        let containerClasses = 'w-full';

        // Apply section-specific sizing
        if (isMetric) {
            containerClasses += ' h-full min-h-[120px]';
        } else if (isTable) {
            containerClasses += ' min-h-96';
        } else {
            containerClasses += ' h-full h-96 min-h-80';
        }

        // In readOnly mode, render without Draggable wrapper
        if (readOnly) {
            return (
                <div key={widget.id} className={containerClasses}>
                    <WidgetCard
                        widget={widget}
                        onDelete={handleDelete}
                        readOnly={true}
                    />
                </div>
            );
        }

        return (
            <Draggable key={widget.id} draggableId={widget.id.toString()} index={index}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`${containerClasses} transition-all duration-200 ${snapshot.isDragging
                            ? 'rotate-1 scale-105 z-50 shadow-2xl opacity-90 ring-2 ring-primary/30'
                            : ''
                            }`}
                        style={{
                            ...provided.draggableProps.style,
                            transform: snapshot.isDragging
                                ? `${provided.draggableProps.style?.transform} rotate(1deg)`
                                : provided.draggableProps.style?.transform
                        }}
                    >
                        <WidgetCard
                            widget={widget}
                            onDelete={handleDelete}
                            onAskMore={onAskMore}
                            dragHandleProps={provided.dragHandleProps}
                            isDragging={snapshot.isDragging}
                        />
                    </div>
                )}
            </Draggable>
        );
    };

    return (
        <>
            {/* Save Status Indicator */}
            {(isSavingOrder || saveError) && (
                <div className={`mb-6 p-3 rounded-lg flex items-center gap-2 text-sm ${saveError
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                    }`}>
                    {isSavingOrder ? (
                        <>
                            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <span>Saving widget arrangement...</span>
                        </>
                    ) : saveError ? (
                        <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span>{saveError}</span>
                            <button
                                onClick={() => setSaveError(null)}
                                className="ml-auto hover:opacity-70"
                            >
                                ×
                            </button>
                        </>
                    ) : null}
                </div>
            )}

            <DragDropContext onDragEnd={handleOnDragEnd}>
                <div className="space-y-1">
                    {/* 1. METRICS SECTION - Small KPI Cards */}
                    {metrics.length > 0 && (
                        <Droppable droppableId="metrics-section">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`transition-all duration-200 rounded-lg ${snapshot.isDraggingOver
                                        ? 'bg-primary/5 border-2 border-primary/20 border-dashed'
                                        : ''
                                        }`}
                                >
                                    <MetricSection
                                        widgets={metrics}
                                        onDelete={handleDelete}
                                        onAskMore={onAskMore}
                                        renderWidget={renderWidget}
                                    />
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )}

                    {/* 2. CHARTS SECTION - Medium Height Visualizations */}
                    {charts.length > 0 && (
                        <Droppable droppableId="charts-section">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`transition-all duration-200 rounded-lg ${snapshot.isDraggingOver
                                        ? 'bg-primary/5 border-2 border-primary/20 border-dashed'
                                        : ''
                                        }`}
                                >
                                    <ChartSection
                                        widgets={charts}
                                        onDelete={handleDelete}
                                        onAskMore={onAskMore}
                                        renderWidget={renderWidget}
                                    />
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )}

                    {/* 3. TABLES SECTION - Large Data Widgets */}
                    {tables.length > 0 && (
                        <Droppable droppableId="tables-section">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`transition-all duration-200 rounded-lg ${snapshot.isDraggingOver
                                        ? 'bg-primary/5 border-2 border-primary/20 border-dashed'
                                        : ''
                                        }`}
                                >
                                    <TableSection
                                        widgets={tables}
                                        onDelete={handleDelete}
                                        onAskMore={onAskMore}
                                        renderWidget={renderWidget}
                                    />
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )}

                    {/* Empty Drop Zones for sections that don't exist — hidden in readOnly */}
                    {!readOnly && visibleWidgets.length > 0 && (
                        <>
                            {/* Empty Metrics Section */}
                            {metrics.length === 0 && (
                                <Droppable droppableId="metrics-section">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`min-h-[60px] rounded-lg border-2 border-dashed transition-all duration-200 flex items-center justify-center mb-4 ${snapshot.isDraggingOver
                                                ? 'bg-primary/5 border-primary/40'
                                                : 'border-slate-200 dark:border-slate-700 bg-slate-50/30 dark:bg-slate-800/30'
                                                }`}
                                        >
                                            <div className="text-center text-slate-400 dark:text-slate-500 text-sm">
                                                <span className="material-symbols-outlined text-2xl">
                                                    analytics
                                                </span>
                                                <p>Drop metrics and KPI widgets here</p>
                                            </div>
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            )}

                            {/* Empty Charts Section */}
                            {charts.length === 0 && (
                                <Droppable droppableId="charts-section">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`min-h-[80px] rounded-lg border-2 border-dashed transition-all duration-200 flex items-center justify-center mb-4 ${snapshot.isDraggingOver
                                                ? 'bg-primary/5 border-primary/40'
                                                : 'border-slate-200 dark:border-slate-700 bg-slate-50/30 dark:bg-slate-800/30'
                                                }`}
                                        >
                                            <div className="text-center text-slate-400 dark:text-slate-500 text-sm">
                                                <span className="material-symbols-outlined text-2xl">
                                                    bar_chart
                                                </span>
                                                <p>Drop chart and visualization widgets here</p>
                                            </div>
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            )}

                            {/* Empty Tables Section */}
                            {tables.length === 0 && (
                                <Droppable droppableId="tables-section">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`min-h-[80px] rounded-lg border-2 border-dashed transition-all duration-200 flex items-center justify-center mb-4 ${snapshot.isDraggingOver
                                                ? 'bg-primary/5 border-primary/40'
                                                : 'border-slate-200 dark:border-slate-700 bg-slate-50/30 dark:bg-slate-800/30'
                                                }`}
                                        >
                                            <div className="text-center text-slate-400 dark:text-slate-500 text-sm">
                                                <span className="material-symbols-outlined text-2xl">
                                                    table
                                                </span>
                                                <p>Drop table and data widgets here</p>
                                            </div>
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            )}
                        </>
                    )}

                    {/* Empty State - When no widgets exist */}
                    {visibleWidgets.length === 0 && (
                        <div className="text-center py-16">
                            <div className="text-slate-400 dark:text-slate-500">
                                <span className="material-symbols-outlined text-6xl mb-4 block opacity-30">
                                    dashboard
                                </span>
                                <h3 className="text-xl font-medium mb-2">No widgets available</h3>
                                <p className="text-sm opacity-75">Create your first widget to get started with your dashboard</p>
                            </div>
                        </div>
                    )}
                </div>
            </DragDropContext>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                widgetTitle={deleteModal.widget?.title || ''}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isDeleting={deleteModal.isDeleting}
            />

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </>
    );
});

WidgetGrid.displayName = 'WidgetGrid';
